import { z } from 'zod'
import type { PlacedPart } from './assembly'
import type { AssemblyConnection } from './connection'

export const RobotGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  instanceIds: z.array(z.string()).min(2),
})

export type RobotGroup = z.infer<typeof RobotGroupSchema>

export function connectedInstanceIds(
  parts: PlacedPart[],
  connections: AssemblyConnection[],
  instanceId: string,
) {
  const existingIds = new Set(parts.map((part) => part.instanceId))
  if (!existingIds.has(instanceId)) return []
  const adjacent = new Map<string, Set<string>>()
  for (const connection of connections) {
    if (!existingIds.has(connection.fromPartId) || !existingIds.has(connection.toPartId)) continue
    const from = adjacent.get(connection.fromPartId) ?? new Set<string>()
    from.add(connection.toPartId)
    adjacent.set(connection.fromPartId, from)
    const to = adjacent.get(connection.toPartId) ?? new Set<string>()
    to.add(connection.fromPartId)
    adjacent.set(connection.toPartId, to)
  }
  const connected = new Set([instanceId])
  const pending = [instanceId]
  while (pending.length) {
    const current = pending.pop()!
    for (const neighbor of adjacent.get(current) ?? []) {
      if (connected.has(neighbor)) continue
      connected.add(neighbor)
      pending.push(neighbor)
    }
  }
  return parts
    .map((part) => part.instanceId)
    .filter((id) => connected.has(id))
}

export function normalizeRobotGroups(
  groups: RobotGroup[],
  parts: PlacedPart[],
  connections: AssemblyConnection[],
) {
  const claimed = new Set<string>()
  return groups.flatMap((group) => {
    const members = group.instanceIds.filter((id) => !claimed.has(id))
    if (members.length < 2) return []
    const connectedIds = connectedInstanceIds(parts, connections, members[0])
    const connected = new Set(connectedIds)
    if (!members.every((id) => connected.has(id))) return []
    const expandedMembers = connectedIds.filter((id) => !claimed.has(id))
    if (expandedMembers.length < 2) return []
    expandedMembers.forEach((id) => claimed.add(id))
    return [{ ...group, instanceIds: expandedMembers }]
  })
}

export function robotGroupForPart(groups: RobotGroup[], instanceId: string | null) {
  return instanceId
    ? groups.find((group) => group.instanceIds.includes(instanceId)) ?? null
    : null
}
